// src/components/Lawyer/LawyerDetailsModal.jsx
import React, { useMemo, useState } from "react";
import SelectCategory from "../../components/Common/SelectCategory";
import {
  Modal,
  Button,
  Row,
  Col,
  Badge,
  Table,
  Placeholder,
  Tab,
  Nav,
  Form,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  MapPin,
  GraduationCap,
  Briefcase,
  Eye,
  Download,
  Trash2,
  UploadCloud,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileCode,
  UserCheck, // ✅ nuevo icono para tutor (opcional)
} from "lucide-react";
import Swal from "sweetalert2";
import usePermission from "../../hooks/usePermission";
import { formatDateTime } from "../../utils/formatters";
import { openFileInNewTab, saveFile } from "../../services/fileService";
import {
  uploadLawyerAttachments,
  deleteLawyerAttachment,
  getLawyerReport,
} from "../../services/lawyerService";

import { pdf } from "@react-pdf/renderer";
import LawyerReportPDF from "../../components/Lawyer/LawyerReportPDF";

const safe = (val, placeholder = "—") =>
  val === null || val === undefined || val === "" ? placeholder : val;

const getInitials = (fullName) => {
  if (!fullName) return "NA";
  const parts = fullName.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const DlItem = ({ label, value }) => (
  <Row className="py-2 border-bottom align-items-center">
    <Col md={4} className="text-muted small fw-semibold text-uppercase">
      {label}
    </Col>
    <Col md={8} className="fw-normal text-success">
      {value}
    </Col>
  </Row>
);

const LoadingBlock = () => (
  <div>
    {[...Array(6)].map((_, i) => (
      <Row className="py-2" key={i}>
        <Col md={4}>
          <Placeholder animation="wave">
            <Placeholder xs={6} />
          </Placeholder>
        </Col>
        <Col md={8}>
          <Placeholder animation="wave">
            <Placeholder xs={10} />
          </Placeholder>
        </Col>
      </Row>
    ))}
  </div>
);

// Ícono por tipo/extension
const getFileIcon = (contentType, fileName) => {
  const ext = (fileName || "").toLowerCase();
  const type = (contentType || "").toLowerCase();
  if (type.includes("image/") || /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(ext))
    return <FileImage size={18} />;
  if (type.includes("video/") || /\.(mp4|mov|avi|mkv|webm)$/.test(ext))
    return <FileVideo size={18} />;
  if (type.includes("audio/") || /\.(mp3|wav|aac|ogg)$/.test(ext))
    return <FileAudio size={18} />;
  if (type.includes("zip") || /\.(zip|rar|7z|tar|gz)$/.test(ext))
    return <FileArchive size={18} />;
  if (type.includes("pdf") || /\.pdf$/.test(ext)) return <FileText size={18} />;
  if (
    type.includes("msword") ||
    type.includes("wordprocessingml") ||
    /\.(doc|docx)$/.test(ext)
  )
    return <FileText size={18} />;
  if (type.includes("spreadsheet") || /\.(xls|xlsx|csv)$/.test(ext))
    return <FileText size={18} />;
  if (
    type.includes("json") ||
    type.includes("xml") ||
    /\.(json|xml|yml|yaml|txt|md|log)$/.test(ext)
  )
    return <FileCode size={18} />;
  return <File size={18} />;
};

const LawyerDetailsModal = ({
  visible,
  onClose,
  data,
  loading = false,
  onReload,
}) => {
  const hasPermission = usePermission();
  const isActive = !!data?.isActive;

  // ✅ detectar estudiante (soporta academicLevelCode si existe, sino academicLevelName)
  const isStudent = useMemo(() => {
    const code = (data?.academicLevelCode || "")
      .toString()
      .trim()
      .toUpperCase();
    if (code) return code === "ESTUDIANTE";
    const name = (data?.academicLevelName || "")
      .toString()
      .trim()
      .toUpperCase();
    return name === "ESTUDIANTE";
  }, [data?.academicLevelCode, data?.academicLevelName]);

  const tutorLabelValue = useMemo(() => {
    // preferimos nombre, si no, el Id
    const name = data?.tutorLawyerName;
    const id = data?.tutorLawyerId;
    if (name) return name;
    if (id) return id;
    return "—";
  }, [data?.tutorLawyerName, data?.tutorLawyerId]);

  const canUpload = useMemo(
    () =>
      hasPermission("abogados.attachments.create") ||
      hasPermission("abogados.update"),
    [hasPermission]
  );
  const canDelete = useMemo(
    () =>
      hasPermission("abogados.attachments.delete") ||
      hasPermission("abogados.update"),
    [hasPermission]
  );

  const [isUploading, setIsUploading] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [attachmentTypeId, setAttachmentTypeId] = useState("");
  const [notes, setNotes] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const MAX_NOTES = 300;
  const filesArray = Array.from(filesToUpload || []);
  const canSend =
    attachmentTypeId &&
    filesArray.length > 0 &&
    !isUploading &&
    notes.length <= MAX_NOTES;

  const refreshAfterMutation = async () => {
    if (typeof onReload === "function") {
      await onReload();
    } else {
      Swal.fire("Actualiza", "Vuelve a abrir para ver cambios.", "info");
    }
  };

  const handleUpload = async () => {
    if (!data?.id) return;
    if (!attachmentTypeId) {
      Swal.fire("Tipo requerido", "Selecciona un tipo de adjunto.", "warning");
      return;
    }
    if (!filesToUpload || filesArray.length === 0) {
      Swal.fire(
        "Archivo requerido",
        "Selecciona uno o más archivos.",
        "warning"
      );
      return;
    }

    try {
      setIsUploading(true);
      const resp = await uploadLawyerAttachments(
        data.id,
        filesArray,
        attachmentTypeId,
        notes
      );
      if (!resp?.success)
        throw new Error(resp?.message || "No se pudo subir el/los archivo(s).");

      Swal.fire("Éxito", "Adjunto(s) subido(s) correctamente.", "success");
      setFilesToUpload([]);
      setAttachmentTypeId("");
      setNotes("");
      await refreshAfterMutation();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.message || "No se pudo subir el/los archivo(s).",
        "error"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!data?.id) return;

    const confirm = await Swal.fire({
      title: "¿Eliminar adjunto?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    try {
      const resp = await deleteLawyerAttachment(attachmentId);
      if (!resp?.success)
        throw new Error(resp?.message || "No se pudo eliminar el adjunto.");

      Swal.fire("Eliminado", "El adjunto fue eliminado.", "success");
      await refreshAfterMutation();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.message || "No se pudo eliminar el adjunto.",
        "error"
      );
    }
  };

  const handleOpenPDF = async () => {
    if (!data?.id) return;
    try {
      setPdfLoading(true);
      const resp = await getLawyerReport(data.id);
      if (!resp?.success)
        throw new Error(resp?.message || "No se pudo obtener el reporte.");
      const blob = await pdf(<LawyerReportPDF data={resp.data} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      Swal.fire("Error", "No se pudo generar el PDF.", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Modal show={visible} onHide={onClose} size="lg" centered scrollable>
      {/* HEADER */}
      <div className="d-flex align-items-center gap-3 p-4 lawyer-header">
        <div
          className="rounded-circle shadow-sm d-flex align-items-center justify-content-center border"
          style={{
            width: 70,
            height: 70,
            background: "var(--bs-body-bg)",
            fontSize: "1.5rem",
            color: "#0d6efd",
            fontWeight: 600,
          }}
        >
          {getInitials(data?.fullName)}
        </div>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h4 className="mb-0 fw-semibold text-body">
              {safe(data?.fullName)}
            </h4>

            <Badge
              bg={isActive ? "success" : "danger"}
              pill
              className="text-light fw-semibold"
            >
              {isActive ? "Activo" : "Inactivo"}
            </Badge>

            {/* ✅ Badge de Estudiante */}
            {isStudent && (
              <Badge bg="info" pill className="text-dark fw-semibold">
                Estudiante
              </Badge>
            )}
          </div>
          <div className="text-muted small mt-1">{safe(data?.email)}</div>
        </div>
      </div>

      {/* BODY */}
      <Modal.Body className="pt-3 px-4">
        <Tab.Container defaultActiveKey="resumen">
          <Nav
            variant="pills"
            className="mb-4 flex-wrap gap-2 justify-content-start"
          >
            <Nav.Item>
              <Nav.Link eventKey="resumen">Resumen</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="personal">Personal</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="direccion">Dirección</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="profesional">Profesional</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="educacion">Educación</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="adjuntos">Adjuntos</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="sistema">Sistema</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* === RESUMEN === */}
            <Tab.Pane eventKey="resumen">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <Row className="gy-3">
                    <Col md={6}>
                      <DlItem
                        label="Documento"
                        value={safe(data?.documentNumber)}
                      />
                    </Col>
                    <Col md={6}>
                      <DlItem label="Empresa" value={safe(data?.companyName)} />
                    </Col>

                    {/* ✅ Tutor en resumen si es estudiante */}
                    {isStudent && (
                      <Col md={12}>
                        <DlItem
                          label="Tutor responsable"
                          value={
                            <span className="d-flex align-items-center gap-2">
                              <UserCheck size={16} />
                              {tutorLabelValue}
                            </span>
                          }
                        />
                      </Col>
                    )}

                    {/* ✅ Licencia solo si NO es estudiante */}
                    {!isStudent && (
                      <Col md={6}>
                        <DlItem
                          label="Tarjeta Profesional"
                          value={safe(data?.professionalLicenseNumber)}
                        />
                      </Col>
                    )}

                    <Col md={6}>
                      <DlItem
                        label="Fecha nacimiento"
                        value={formatDateTime(data?.birthDate)}
                      />
                    </Col>

                    <Col md={12}>
                      <div className="pt-2">
                        <span className="text-muted small text-uppercase">
                          Especialidades
                        </span>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {Array.isArray(data?.specialtyNames) &&
                          data.specialtyNames.length > 0 ? (
                            data.specialtyNames.map((name, idx) => (
                              <Badge
                                key={idx}
                                pill
                                style={{
                                  backgroundColor: "var(--specialty-bg)",
                                  color: "var(--specialty-text)",
                                }}
                                className="shadow-sm specialty-badge"
                              >
                                {name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted">
                              Sin especialidades
                            </span>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </Tab.Pane>

            {/* === PERSONAL === */}
            <Tab.Pane eventKey="personal">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <DlItem label="Nombres" value={safe(data?.firstName)} />
                  <DlItem label="Apellidos" value={safe(data?.lastName)} />
                  <DlItem
                    label="Tipo Documento"
                    value={safe(data?.documentTypeName)}
                  />
                  <DlItem
                    label="Número Documento"
                    value={safe(data?.documentNumber)}
                  />
                  <DlItem
                    label="Fecha Nacimiento"
                    value={formatDateTime(data?.birthDate)}
                  />
                  <DlItem label="Género" value={safe(data?.genderName)} />
                  <DlItem
                    label="Teléfono móvil"
                    value={safe(data?.phoneMobile)}
                  />
                  <DlItem
                    label="Teléfono alterno"
                    value={safe(data?.phoneAlt)}
                  />
                </div>
              )}
            </Tab.Pane>

            {/* === DIRECCIÓN === */}
            <Tab.Pane eventKey="direccion">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <h6 className="mb-3 text-primary d-flex align-items-center gap-2">
                    <MapPin size={16} /> Ubicación
                  </h6>
                  <DlItem
                    label="Dirección 1"
                    value={safe(data?.addressLine1)}
                  />
                  <DlItem
                    label="Dirección 2"
                    value={safe(data?.addressLine2)}
                  />
                  <DlItem label="País" value={safe(data?.countryName)} />
                  <DlItem
                    label="Departamento"
                    value={safe(data?.departmentName)}
                  />
                  <DlItem
                    label="Municipio"
                    value={safe(data?.municipalityName)}
                  />
                </div>
              )}
            </Tab.Pane>

            {/* === PROFESIONAL === */}
            <Tab.Pane eventKey="profesional">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <h6 className="mb-3 text-primary d-flex align-items-center gap-2">
                    <Briefcase size={16} /> Información profesional
                  </h6>

                  <DlItem
                    label="Nivel académico"
                    value={safe(data?.academicLevelName)}
                  />

                  {/* ✅ Tutor visible aquí también si estudiante */}
                  {isStudent && (
                    <DlItem
                      label="Tutor responsable"
                      value={
                        <span className="d-flex align-items-center gap-2">
                          <UserCheck size={16} />
                          {tutorLabelValue}
                        </span>
                      }
                    />
                  )}

                  <DlItem
                    label="Título principal"
                    value={safe(data?.mainDegreeTitle)}
                  />
                  <DlItem
                    label="Institución"
                    value={safe(data?.mainInstitution)}
                  />

                  {/* ✅ Bloque de licencia SOLO si NO es estudiante */}
                  {!isStudent && (
                    <>
                      <DlItem
                        label="Fecha graduación"
                        value={formatDateTime(data?.mainGraduationDate)}
                      />
                      <DlItem
                        label="Tarjeta profesional"
                        value={safe(data?.professionalLicenseNumber)}
                      />
                      <DlItem
                        label="Fecha expedición"
                        value={formatDateTime(
                          data?.professionalLicenseIssueDate
                        )}
                      />
                      <DlItem
                        label="Expedido por"
                        value={safe(data?.professionalLicenseIssuedBy)}
                      />
                      <DlItem
                        label="Licencia verificada"
                        value={
                          <Badge
                            bg={data?.isLicenseVerified ? "success" : "danger"}
                            pill
                          >
                            {data?.isLicenseVerified ? "Si" : "No"}
                          </Badge>
                        }
                      />
                    </>
                  )}

                  {/* ✅ si es estudiante, dejamos una nota clara */}
                  {isStudent && (
                    <div className="mt-2 text-muted small">
                      Este abogado está marcado como <strong>ESTUDIANTE</strong>
                      , por lo tanto no requiere tarjeta profesional ni fecha de
                      expedición.
                    </div>
                  )}

                  <DlItem label="Resumen" value={safe(data?.summary)} />
                </div>
              )}
            </Tab.Pane>

            {/* === EDUCACIÓN === */}
            <Tab.Pane eventKey="educacion">
              {loading ? (
                <LoadingBlock />
              ) : Array.isArray(data?.educations) &&
                data.educations.length > 0 ? (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <h6 className="mb-3 text-primary d-flex align-items-center gap-2">
                    <GraduationCap size={16} /> Formación académica
                  </h6>
                  <Table responsive hover className="align-middle mb-0">
                    <thead className="table-light">
                      <tr className="text-center">
                        <th>Título</th>
                        <th>Institución</th>
                        <th>Nivel</th>
                        <th>Inicio</th>
                        <th>Graduación</th>
                        <th>Graduado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.educations.map((e) => (
                        <tr key={e.id}>
                          <td>{safe(e.titleName)}</td>
                          <td>{safe(e.institution)}</td>
                          <td>{safe(e.academicLevelName)}</td>
                          <td className="text-center">
                            {formatDateTime(e.startDate)}
                          </td>
                          <td className="text-center">
                            {formatDateTime(e.graduationDate)}
                          </td>
                          <td className="text-center">
                            {e.isGraduated ? "Sí" : "No"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-muted">Sin registros de educación.</div>
              )}
            </Tab.Pane>

            {/* === ADJUNTOS === */}
            <Tab.Pane eventKey="adjuntos">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 text-primary">Adjuntos</h6>
                  </div>

                  {canUpload && (
                    <div className="border rounded-3 p-3 mb-3 bg-body">
                      <Row className="g-3">
                        <Col md={4}>
                          <Form.Label className="small text-muted">
                            Tipo de adjunto
                          </Form.Label>
                          <SelectCategory
                            categoryCode="lawyer_doc_type"
                            value={attachmentTypeId}
                            onChange={(val) => setAttachmentTypeId(val)}
                            placeholder="Seleccione tipo de adjunto..."
                            showCode={false}
                          />
                        </Col>

                        <Col md={4}>
                          <Form.Label className="small text-muted">
                            Archivo(s)
                          </Form.Label>
                          <Form.Control
                            type="file"
                            multiple
                            onChange={(e) => setFilesToUpload(e.target.files)}
                          />
                          {filesArray.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {filesArray.map((f, i) => (
                                <span
                                  key={i}
                                  className="badge rounded-pill text-bg-light border"
                                >
                                  {f.name.length > 32
                                    ? `${f.name.slice(0, 29)}…`
                                    : f.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </Col>

                        <Col md={4}>
                          <Form.Label className="small text-muted">
                            Notas (opcional)
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Agrega una nota para este adjunto…"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            maxLength={MAX_NOTES}
                          />
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <small className="text-muted">
                              {notes.length}/{MAX_NOTES}
                            </small>
                            <Button
                              variant="success"
                              className="d-flex align-items-center gap-2"
                              onClick={handleUpload}
                              disabled={!canSend}
                            >
                              {isUploading ? (
                                <Spinner size="sm" />
                              ) : (
                                <UploadCloud size={16} />
                              )}
                              {isUploading ? "Subiendo..." : "Subir"}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {Array.isArray(data?.attachments) &&
                  data.attachments.length > 0 ? (
                    <Table responsive hover className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="text-nowrap">Tipo</th>
                          <th>Archivo</th>
                          <th>Notas</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.attachments.map((a) => (
                          <tr key={a.id}>
                            <td className="text-nowrap">
                              <div className="d-flex flex-column">
                                <span className="fw-semibold">
                                  {safe(a.attachmentTypeName)}
                                </span>
                                <small className="text-muted">
                                  {safe(a.attachmentTypeCode)}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {getFileIcon(a.contentType, a.fileName)}
                                <span className="text-break">
                                  {safe(a.fileName)}
                                </span>
                              </div>
                            </td>
                            <td>
                              {a?.notes ? (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tt-${a.id}`}>
                                      {a.notes}
                                    </Tooltip>
                                  }
                                >
                                  <span
                                    className="text-truncate d-inline-block"
                                    style={{ maxWidth: 260 }}
                                  >
                                    {a.notes.length > 60
                                      ? `${a.notes.slice(0, 57)}…`
                                      : a.notes}
                                  </span>
                                </OverlayTrigger>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="d-inline-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  title="Ver"
                                  onClick={() => openFileInNewTab(a.fileId)}
                                >
                                  <Eye size={16} />
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  title="Descargar"
                                  onClick={() => saveFile(a.fileId)}
                                >
                                  <Download size={16} />
                                </Button>
                                {canDelete && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    title="Eliminar"
                                    onClick={() => handleDeleteAttachment(a.id)}
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-muted">Sin adjuntos cargados.</div>
                  )}
                </div>
              )}
            </Tab.Pane>

            {/* === SISTEMA === */}
            <Tab.Pane eventKey="sistema">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <DlItem label="ID" value={safe(data?.id)} />
                  <DlItem
                    label="SecurityUserId"
                    value={safe(data?.securityUserId)}
                  />
                  <DlItem label="Empresa (ID)" value={safe(data?.companyId)} />
                  <DlItem label="Empresa" value={safe(data?.companyName)} />
                  <DlItem
                    label="Creado"
                    value={formatDateTime(data?.createdAt)}
                  />
                  <DlItem
                    label="Actualizado"
                    value={formatDateTime(data?.updatedAt)}
                  />
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4">
        <Button
          variant="outline-primary"
          className="d-flex align-items-center gap-2"
          onClick={handleOpenPDF}
          disabled={pdfLoading || loading || !data?.id}
        >
          {pdfLoading ? <Spinner size="sm" /> : <Download size={16} />}
          {pdfLoading ? "Generando PDF..." : "Generar PDF"}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Footer>

      <style jsx global>{`
        .lawyer-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 1px solid var(--bs-border-color);
        }
        [data-bs-theme="dark"] .lawyer-header {
          background: linear-gradient(135deg, #1e1e1e 0%, #2b2b2b 100%);
        }
        [data-bs-theme="light"] {
          --specialty-bg: #d4edda;
          --specialty-text: #155724;
          --table-head-bg: #f1f3f5;
          --table-head-text: #212529;
        }
        [data-bs-theme="dark"] {
          --specialty-bg: #1b4332;
          --specialty-text: #b7e4c7;
          --table-head-bg: #222;
          --table-head-text: #8ce99a;
        }
        table thead {
          background-color: var(--table-head-bg) !important;
          color: var(--table-head-text) !important;
        }
        .specialty-badge {
          font-size: 0.7rem;
          padding: 0.45rem 0.9rem;
          font-weight: 600;
          border-radius: 30rem;
          letter-spacing: 0.3px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .specialty-badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        }
        .badge.text-bg-light.border {
          background: var(--bs-body-bg);
          color: var(--bs-body-color);
          border-color: var(--bs-border-color);
          font-weight: 500;
          padding: 0.5rem 0.6rem;
        }
        [data-bs-theme="dark"] .badge.text-bg-light.border {
          background: #262b33;
        }
        textarea.form-control:focus {
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
        }
      `}</style>
    </Modal>
  );
};

export default LawyerDetailsModal;
